import React, { useState } from 'react';
import { CircularProgress, TextField } from '@mui/material';
import academicYearsApi from '../../apis/academicYears';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import DialogModal from '../../components/DialogModal';
import { SnackbarSeverityType } from '../../enums';
import { FormColumn, DialogActionsRow, ActionButton } from './styled';
import { AddAcademicYearDialogProps } from './types/AcademicYearTypes';
import { UpsertAcademicYearDatesBody } from '../../types/academicYearType';
import { useAcademicYearValidation } from './utils/useValidation';
import SelectAll from '../../components/SelectAllField/SelectAllField';

const AddAcademicYearDialog: React.FC<AddAcademicYearDialogProps> = ({
  open,
  onClose,
  onSaved,
  gradeOptions,
  existingGrades = [],
  academicYear,
  showToast
}) => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { errors, setErrors, validate } = useAcademicYearValidation();

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    if (errors.startDate) {
      setErrors((prev) => ({ ...prev, startDate: undefined }));
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    if (errors.endDate) {
      setErrors((prev) => ({ ...prev, endDate: undefined }));
    }
  };

  const handleClose = () => {
    setSelectedGrades([]);
    setStartDate('');
    setEndDate('');
    setErrors({});
    onClose();
  };

  const availableGradeOptions = gradeOptions.filter(
    (grade) => !existingGrades.includes(grade)
  );
  const allGradesConfigured = availableGradeOptions.length === 0;
  const gradeOptionsForSelect = availableGradeOptions.map((grade) => ({
    value: grade,
    label: grade
  }));

  const isSaveDisabled =
    allGradesConfigured ||
    loading ||
    selectedGrades.length === 0 ||
    !startDate ||
    !endDate;

  const handleSave = async () => {
    const isValidForm = await validate({
      startDate,
      endDate
    });
    if (!isValidForm) return;

    const payload: UpsertAcademicYearDatesBody = {
      grades: selectedGrades,
      academicYearDates: {
        [academicYear]: {
          startDate,
          endDate
        }
      }
    };

    try {
      setLoading(true);
      await academicYearsApi.upsertAcademicYears(
        loggedInUser!.school!,
        payload
      );
      showToast('Academic year saved.', SnackbarSeverityType.success);
      onSaved();
      handleClose();
    } catch (err) {
      console.error(err);
      showToast('Failed to save academic year.', SnackbarSeverityType.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogModal
      open={open}
      onClose={handleClose}
      title={'Add Academic Year Dates ( ' + academicYear + ' )'}
      maxWidth='sm'
      actions={
        allGradesConfigured ? undefined : (
          <DialogActionsRow>
            <ActionButton
              onClick={handleClose}
              disabled={loading}
              variant='contained'
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleSave}
              disabled={isSaveDisabled}
              variant='contained'
            >
              {loading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                'Save'
              )}
            </ActionButton>
          </DialogActionsRow>
        )
      }
    >
      {allGradesConfigured ? (
        <FormColumn>
          Academic Year Dates for all grades are added for {academicYear}
        </FormColumn>
      ) : (
        <FormColumn>
          <SelectAll
            label='Grades'
            placeholder='Select Grades'
            options={gradeOptionsForSelect}
            selected={selectedGrades}
            onChange={setSelectedGrades}
          />
          <TextField
            label='Start Date'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={handleStartDateChange}
            error={Boolean(errors.startDate)}
            helperText={errors.startDate}
            fullWidth
          />
          <TextField
            label='End Date'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={handleEndDateChange}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate}
            fullWidth
          />
        </FormColumn>
      )}
    </DialogModal>
  );
};

export default AddAcademicYearDialog;
